import L from 'leaflet';
import { decodePolyline } from './utils/polyline.js';
import { AnimationController } from './animation/AnimationController.js';
import { GifExporter } from './export/GifExporter.js';
import { StravaAuth } from './auth/StravaAuth.js';
import { StravaAPI } from './api/StravaAPI.js';
import { OnboardingUI } from './ui/OnboardingUI.js';

// Initialize auth and API
const auth = new StravaAuth();
const api = new StravaAPI(auth);

// Initialize map
const map = L.map('map').setView([0, 0], 2);

// Add CartoDB Positron tiles (gray/minimal style perfect for activity visualization)
L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  subdomains: 'abcd',
  maxZoom: 20
}).addTo(map);

// Update stats when map bounds change (pan/zoom)
map.on('moveend', () => {
  if (activities.length > 0) {
    updateStats();
    scheduleURLUpdate(); // Update URL with new map position
  }
});

// State
let activities = [];
let polylines = [];
let animationController = null;
let gifExporter = null;
let captureBox = {
  ratio: 'max',
  bounds: null
};

// Color palette for selection
const COLOR_PALETTE = [
  { name: 'Orange', value: '#fc4c02' },
  { name: 'Blue', value: '#0066cc' },
  { name: 'Cyan', value: '#00cccc' },
  { name: 'Green', value: '#66cc00' },
  { name: 'Brown', value: '#996600' },
  { name: 'Red', value: '#e74c3c' },
  { name: 'Purple', value: '#9b59b6' },
  { name: 'Pink', value: '#e91e63' },
  { name: 'Yellow', value: '#f1c40f' },
  { name: 'Lime', value: '#d4e157' },
  { name: 'Teal', value: '#00a8cc' },
  { name: 'Navy', value: '#006994' },
  { name: 'Forest', value: '#2d5016' },
  { name: 'Light Orange', value: '#ff6b35' },
  { name: 'Gray', value: '#888888' },
  { name: 'Black', value: '#1a1a1a' }
];

// Default colors for common activity types
const DEFAULT_ACTIVITY_COLORS = {
  'Run': '#fc4c02',
  'Ride': '#0066cc',
  'Swim': '#00cccc',
  'Walk': '#66cc00',
  'Hike': '#996600',
  'default': '#888888'
};

// Custom colors per activity type (user can change these)
let customActivityColors = {};

// Get current activity colors
function getActivityColors() {
  return customActivityColors;
}

// Get the dominant activity color (from the activity type with the most activities)
function getDominantActivityColor() {
  const filtered = getFilteredActivities();
  if (filtered.length === 0) return '#2196F3'; // Default blue

  // Count activities by type
  const typeCounts = {};
  filtered.forEach(activity => {
    const type = activity.type;
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });

  // Find the type with the most activities
  let dominantType = null;
  let maxCount = 0;
  for (const [type, count] of Object.entries(typeCounts)) {
    if (count > maxCount) {
      maxCount = count;
      dominantType = type;
    }
  }

  // Get the color for that type
  if (dominantType && customActivityColors[dominantType]) {
    return customActivityColors[dominantType];
  }

  return '#2196F3'; // Default blue
}

// Get selected corner for date overlay
function getSelectedDateCorner() {
  const selectedOption = document.querySelector('.corner-option.selected');
  return selectedOption ? selectedOption.getAttribute('data-corner') : 'bottom-right';
}

// Format date based on selected format
function formatDateOverlay(date, format = null) {
  if (!date) return '';

  // Use current selection if no format specified
  if (!format) {
    format = dateFormatSelect ? dateFormatSelect.value : 'D MMMM YYYY';
  }

  const day = date.getDate(); // No leading zero
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  const month2Digit = String(monthIndex + 1).padStart(2, '0');

  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthsFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  switch (format) {
    case 'YYYY-MM-DD':
      const day2Digit = String(day).padStart(2, '0');
      return `${year}-${month2Digit}-${day2Digit}`;
    case 'D MMM YYYY':
      return `${day} ${monthsShort[monthIndex]} ${year}`;
    case 'D MMMM YYYY':
      return `${day} ${monthsFull[monthIndex]} ${year}`;
    case 'MMM YYYY':
      return `${monthsShort[monthIndex]} ${year}`;
    case 'MMMM YYYY':
      return `${monthsFull[monthIndex]} ${year}`;
    default:
      return `${day} ${monthsFull[monthIndex]} ${year}`;
  }
}

// Update date format dropdown with examples from current animation date
function updateDateFormatOptions() {
  if (!animationController || !animationController.currentTime || !dateFormatSelect) return;

  const exampleDate = animationController.currentTime;
  const formats = ['YYYY-MM-DD', 'D MMM YYYY', 'D MMMM YYYY', 'MMM YYYY', 'MMMM YYYY'];
  const currentValue = dateFormatSelect.value;

  // Update each option with an example
  Array.from(dateFormatSelect.options).forEach((option, index) => {
    const format = formats[index];
    const example = formatDateOverlay(exampleDate, format);
    option.textContent = example;
    option.value = format;
  });

  // Restore the selected value
  dateFormatSelect.value = currentValue;
}

// Update the date preview overlay on the map
function updateDatePreview() {
  if (!includeDateOverlay.checked || !animationController || !animationController.currentTime) {
    datePreviewOverlay.classList.remove('visible');
    return;
  }

  // Show the preview
  datePreviewOverlay.classList.add('visible');

  // Update the date text
  datePreviewOverlay.textContent = formatDateOverlay(animationController.currentTime);

  // Update the corner position
  const corner = getSelectedDateCorner();
  datePreviewOverlay.className = `visible ${corner}`;

  // Update the color to match dominant activity
  const color = getDominantActivityColor();
  datePreviewOverlay.style.color = color;

  // Update dropdown options with current date
  updateDateFormatOptions();
}

// DOM elements
const loadingEl = document.getElementById('loading');
const loadBtn = document.getElementById('load-btn');
const refreshActivitiesBtn = document.getElementById('refresh-activities-btn');
const logoutBtn = document.getElementById('logout-btn');
const activityTypeAll = document.getElementById('activity-type-all');
const activityTypeList = document.getElementById('activity-type-list');
const statCount = document.getElementById('stat-count');
const statDistance = document.getElementById('stat-distance');
const statTypes = document.getElementById('stat-types');

// Date filter controls
const dateFilterGroup = document.getElementById('date-filter-group');
const filterStartDate = document.getElementById('filter-start-date');
const filterEndDate = document.getElementById('filter-end-date');
const dateFilterResetBtn = document.getElementById('date-filter-reset-btn');

// Animation controls
const animationControlsEl = document.getElementById('animation-controls');
const playBtn = document.getElementById('play-btn');
const pauseBtn = document.getElementById('pause-btn');
const resetBtn = document.getElementById('reset-btn');
const timelineSlider = document.getElementById('timeline-slider');
const speedSlider = document.getElementById('speed-slider');
const speedValue = document.getElementById('speed-value');
const timeDisplay = document.getElementById('time-display');

// Export controls
const exportControlsEl = document.getElementById('export-controls');
const exportBtn = document.getElementById('export-btn');
const exportStartDate = document.getElementById('export-start-date');
const exportEndDate = document.getElementById('export-end-date');
const exportDuration = document.getElementById('export-duration');
const exportWidth = document.getElementById('export-width');
const exportHeight = document.getElementById('export-height');
const exportFps = document.getElementById('export-fps');
const includeDateOverlay = document.getElementById('include-date-overlay');
const includeHeatmapFrame = document.getElementById('include-heatmap-frame');
const dateCornerSelector = document.getElementById('date-corner-selector');
const dateFormatSelect = document.getElementById('date-format-select');
const datePreviewOverlay = document.getElementById('date-preview-overlay');
const sizeEstimateValue = document.getElementById('size-estimate-value');
const exportProgress = document.getElementById('export-progress');
const progressFill = document.getElementById('progress-fill');
const exportStatus = document.getElementById('export-status');


// Main initialization
async function init() {
  // Add onboarding CSS
  const style = document.createElement('style');
  style.textContent = OnboardingUI.getCSS();
  document.head.appendChild(style);

  // Check if we're handling OAuth callback
  if (window.location.search.includes('code=')) {
    try {
      loadingEl.classList.remove('hidden');
      loadingEl.querySelector('div:last-child').textContent = 'Completing authorization...';

      await auth.handleCallback();

      // Show onboarding to fetch activities
      showOnboarding();
    } catch (error) {
      console.error('OAuth callback error:', error);
      alert(`Authorization failed: ${error.message}\n\nPlease try again.`);
      auth.clearAll();
      showOnboarding();
    } finally {
      loadingEl.classList.add('hidden');
    }
    return;
  }

  // Check if user is authenticated
  const status = auth.getStatus();

  // Prioritize cached activities - load them regardless of auth status
  // User can refresh later if they want new data
  if (api.hasCachedActivities()) {
    loadCachedActivities();
  } else if (status.isAuthenticated) {
    // User has auth but no cached data - show onboarding to fetch
    showOnboarding();
  } else {
    // No auth - show onboarding from start
    showOnboarding();
  }
}

// Show onboarding
function showOnboarding() {
  const onboarding = new OnboardingUI(auth, api, (activities) => {
    handleActivitiesLoaded(activities);
  });

  // Determine which step to start at
  const status = auth.getStatus();

  onboarding.show();

  if (status.isAuthenticated) {
    // Skip to fetch step
    onboarding.showStep(5);
  } else if (status.hasCredentials) {
    // Skip to authorization step
    onboarding.showStep(3);
  }
  // Otherwise starts at step 1 (welcome)
}

// Load cached activities
async function loadCachedActivities() {
  try {
    loadingEl.classList.remove('hidden');

    activities = await api.getCachedActivities();

    if (!activities || activities.length === 0) {
      throw new Error('No cached activities found');
    }

    handleActivitiesLoaded(activities);

  } catch (error) {
    console.error('Failed to load cached activities:', error);
    loadingEl.classList.add('hidden');
    showOnboarding();
  }
}

// Handle activities loaded (from cache or fresh fetch)
function handleActivitiesLoaded(loadedActivities) {
  activities = loadedActivities;

  // Remove onboarding container if it exists (it overlays everything)
  const onboardingContainer = document.getElementById('onboarding');
  if (onboardingContainer) {
    onboardingContainer.remove();
  }

  // Update stats
  updateStats();

  // Populate activity type filter
  populateActivityTypes();

  // Initialize date filter with full range (URL restore applied below after restoreStateFromURL)
  initializeDateFilter();

  // Populate color schemes
  populateColorSchemes();

  // Initialize animation
  initializeAnimation();

  // Set default export dates (only on initial load)
  if (animationController.startTime && animationController.endTime) {
    exportStartDate.value = formatDateForInput(animationController.startTime);
    exportEndDate.value = formatDateForInput(animationController.endTime);
  }

  // Set default export dimensions based on current map size (only on initial load)
  updateExportDimensions();

  // Initialize capture box
  initializeCaptureBox();

  // Restore state from URL (after everything is initialized)
  restoreStateFromURL();

  // Apply pending date filter (if restored from URL)
  if (window.pendingDateFilterRestore) {
    const { start, end } = window.pendingDateFilterRestore;
    if (start) filterStartDate.value = start;
    if (end) filterEndDate.value = end;
    delete window.pendingDateFilterRestore;
    initializeAnimation();
  }

  // Apply pending activity type filters (if restored from URL)
  if (window.pendingActivityTypesRestore) {
    const types = window.pendingActivityTypesRestore;
    const allPill = activityTypeAll.parentElement;

    // Uncheck "All"
    activityTypeAll.checked = false;
    allPill.classList.remove('selected');

    // Check the specified types
    document.querySelectorAll('.activity-type-checkbox').forEach(checkbox => {
      const isSelected = types.includes(checkbox.value);
      checkbox.checked = isSelected;
      checkbox.parentElement.classList.toggle('selected', isSelected);
    });

    // Update UI
    populateColorSchemes();
    if (animationController) {
      initializeAnimation();
    } else {
      renderActivities();
    }

    delete window.pendingActivityTypesRestore;
  }

  // Apply pending capture box position (if restored from URL)
  if (window.pendingCaptureBoxRestore) {
    const { left, top, width, height } = window.pendingCaptureBoxRestore;
    const captureBoxEl = document.getElementById('capture-box');
    const captureOverlay = document.getElementById('capture-overlay');

    captureBoxEl.style.left = `${left}px`;
    captureBoxEl.style.top = `${top}px`;
    captureBoxEl.style.width = `${width}px`;
    captureBoxEl.style.height = `${height}px`;

    captureOverlay.style.left = `${left}px`;
    captureOverlay.style.top = `${top}px`;
    captureOverlay.style.width = `${width}px`;
    captureOverlay.style.height = `${height}px`;

    const labelEl = document.getElementById('capture-box-label');
    const aspectRatio = (width / height).toFixed(2);
    labelEl.textContent = `${Math.round(width)}×${Math.round(height)} (${aspectRatio}:1)`;

    captureBox.bounds = { left, top, width, height };

    delete window.pendingCaptureBoxRestore;
  }

  // Apply pending animation time (if restored from URL)
  if (window.pendingAnimationTimeRestore && animationController) {
    const targetDate = new Date(window.pendingAnimationTimeRestore);
    if (!isNaN(targetDate.getTime())) {
      animationController.seek(targetDate);
    }
    delete window.pendingAnimationTimeRestore;
  }

  // Update GIF size estimate after restoring dimensions
  updateGifSizeEstimate();

  // Hide loading
  loadingEl.classList.add('hidden');

  // Show refresh and logout buttons, hide load button
  refreshActivitiesBtn.style.display = 'inline-block';
  logoutBtn.style.display = 'inline-block';
  loadBtn.style.display = 'none';

  // Show instructions popup (only if no URL state was restored)
  if (!window.location.hash) {
    document.getElementById('instructions-popup').classList.add('active');
  }

  // Encode initial state to URL
  scheduleURLUpdate();

  console.log(`Loaded ${activities.length} activities`);

  // Expose to window for testing/experiments (development only)
  window.activities = activities;
  window.gifExporter = gifExporter;
  window.animationController = animationController;
  console.log('Exposed activities, gifExporter, and animationController to window for testing');
}

function updateStats() {
  // Get filtered activities based on type selection
  const selectedTypes = getSelectedActivityTypes();
  let filteredActivities = selectedTypes === 'all'
    ? activities
    : activities.filter(a => selectedTypes.includes(a.type));

  // Filter by map bounds - only include activities with at least one point visible
  const mapBounds = map ? map.getBounds() : null;
  if (mapBounds) {
    filteredActivities = filteredActivities.filter(activity => {
      const polylineStr = activity.map?.summary_polyline;
      if (!polylineStr) return false;

      const coords = decodePolyline(polylineStr);
      // Check if any point is within the current map bounds
      return coords.some(([lat, lng]) => mapBounds.contains([lat, lng]));
    });
  }

  // Calculate stats from filtered activities
  const stats = filteredActivities.reduce((acc, activity) => {
    acc.types.add(activity.type);
    acc.totalDistance += activity.distance || 0;
    return acc;
  }, { types: new Set(), totalDistance: 0 });

  statCount.textContent = filteredActivities.length;
  statDistance.textContent = (stats.totalDistance / 1000).toFixed(2);
  statTypes.textContent = stats.types.size;

  // Update date preview color (in case dominant activity changed)
  updateDatePreview();
}

function initializeDateFilter() {
  const dates = activities
    .filter(a => a.start_date)
    .map(a => new Date(a.start_date));

  if (dates.length === 0) return;

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  const minStr = formatDateForInput(minDate);
  const maxStr = formatDateForInput(maxDate);

  filterStartDate.min = minStr;
  filterStartDate.max = maxStr;
  filterEndDate.min = minStr;
  filterEndDate.max = maxStr;

  // Default to full range
  filterStartDate.value = minStr;
  filterEndDate.value = maxStr;

  dateFilterGroup.style.display = '';
}

function populateActivityTypes() {
  // Only include types that have at least one activity with a polyline
  const typesWithPolylines = activities
    .filter(a => a.map?.summary_polyline)
    .map(a => a.type);
  const types = [...new Set(typesWithPolylines)].sort();

  // Get the parent pills container
  const pillsContainer = activityTypeList.parentElement;

  // Clear all previously generated type pills (keep the "All" pill)
  pillsContainer.querySelectorAll('.activity-pill:not(.all-pill)').forEach(el => el.remove());

  // Create pill for each activity type
  types.forEach(type => {
    const label = document.createElement('label');
    label.className = 'activity-pill';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'activity-type-checkbox';
    checkbox.value = type;
    checkbox.checked = false; // Start unselected (All is selected)

    const span = document.createElement('span');
    span.textContent = type;

    label.appendChild(checkbox);
    label.appendChild(span);

    // Toggle selected class on click
    label.addEventListener('click', (e) => {
      e.preventDefault();
      checkbox.checked = !checkbox.checked;
      label.classList.toggle('selected', checkbox.checked);
      handleActivityTypeChange();
    });

    pillsContainer.appendChild(label);
  });

  // Hide the placeholder div
  activityTypeList.style.display = 'none';
}

// Populate color pickers for each activity type
function populateColorSchemes() {
  const container = document.getElementById('color-schemes-list');
  container.innerHTML = '';

  // Get selected activity types
  const selectedTypes = getSelectedActivityTypes();

  // Get activity types to show in color picker
  let activityTypes;
  if (selectedTypes === 'all') {
    // Show all activity types
    activityTypes = [...new Set(activities.map(a => a.type))].sort();
  } else {
    // Show only selected activity types
    activityTypes = selectedTypes.sort();
  }

  // Initialize custom colors with defaults
  activityTypes.forEach(type => {
    if (!customActivityColors[type]) {
      customActivityColors[type] = DEFAULT_ACTIVITY_COLORS[type] || DEFAULT_ACTIVITY_COLORS.default;
    }
  });

  // Create color picker for each activity type
  activityTypes.forEach(type => {
    const row = document.createElement('div');
    row.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin: 10px 0; padding: 8px; background: #f8f9fa; border-radius: 4px;';

    const typeLabel = document.createElement('span');
    typeLabel.textContent = type;
    typeLabel.style.cssText = 'font-weight: 500; min-width: 80px;';

    const colorSelect = document.createElement('select');
    colorSelect.style.cssText = 'flex: 1; margin: 0 10px; padding: 4px 8px; border-radius: 4px; border: 1px solid #ddd;';

    // Add color options
    COLOR_PALETTE.forEach(color => {
      const option = document.createElement('option');
      option.value = color.value;
      option.textContent = color.name;
      option.style.cssText = `background: ${color.value}; color: white;`;
      if (customActivityColors[type] === color.value) {
        option.selected = true;
      }
      colorSelect.appendChild(option);
    });

    // Color preview box
    const preview = document.createElement('div');
    preview.style.cssText = `width: 30px; height: 30px; background: ${customActivityColors[type]}; border-radius: 4px; border: 2px solid #ddd;`;

    // Update color when selection changes
    colorSelect.addEventListener('change', () => {
      const newColor = colorSelect.value;
      customActivityColors[type] = newColor;
      preview.style.background = newColor;

      // Re-render activities with new colors
      if (animationController) {
        initializeAnimation();
      } else {
        renderActivities();
      }
    });

    row.appendChild(typeLabel);
    row.appendChild(colorSelect);
    row.appendChild(preview);

    container.appendChild(row);
  });
}

// Get selected activity types
function getSelectedActivityTypes() {
  if (activityTypeAll.checked) {
    return 'all';
  }

  const checkboxes = document.querySelectorAll('.activity-type-checkbox:checked');
  return Array.from(checkboxes).map(cb => cb.value);
}

// Get filtered activities based on type
function getFilteredActivities() {
  // Filter by activity type
  const selectedTypes = getSelectedActivityTypes();
  let filtered = selectedTypes === 'all'
    ? activities
    : activities.filter(a => selectedTypes.includes(a.type));

  // Filter by date range
  const filterStart = filterStartDate ? filterStartDate.value : '';
  const filterEnd = filterEndDate ? filterEndDate.value : '';
  if (filterStart) {
    const [y, m, d] = filterStart.split('-');
    const start = new Date(+y, +m - 1, +d); // local midnight
    filtered = filtered.filter(a => new Date(a.start_date) >= start);
  }
  if (filterEnd) {
    const [y, m, d] = filterEnd.split('-');
    const end = new Date(+y, +m - 1, +d, 23, 59, 59, 999); // local end-of-day
    filtered = filtered.filter(a => new Date(a.start_date) <= end);
  }

  return filtered;
}

// Handle date filter changes
function handleDateFilterChange() {
  updateStats();

  if (animationController) {
    initializeAnimation();
  } else {
    renderActivities();
  }

  scheduleURLUpdate();
}

// Handle activity type checkbox changes
function handleActivityTypeChange() {
  const checkboxes = document.querySelectorAll('.activity-type-checkbox');
  const anyChecked = Array.from(checkboxes).some(cb => cb.checked);
  const allPill = document.querySelector('.activity-pill.all-pill');

  // If any individual type is checked, uncheck "All"
  if (anyChecked) {
    activityTypeAll.checked = false;
    allPill.classList.remove('selected');
  } else {
    // If no individual types are checked, check "All Activities"
    activityTypeAll.checked = true;
    allPill.classList.add('selected');
    // Reset all pills to unselected
    document.querySelectorAll('.activity-pill:not(.all-pill)').forEach(pill => {
      pill.classList.remove('selected');
    });
  }

  // Update color schemes to show only selected types
  populateColorSchemes();

  // Update stats to reflect new filter
  updateStats();

  // Re-render activities with new filter
  if (animationController) {
    initializeAnimation();
  } else {
    renderActivities();
  }

  // Update URL with new activity type selection
  scheduleURLUpdate();
}

// Calculate optimal opacity based on activity density in capture box
function calculateActivityOpacity() {
  if (!captureBox.bounds) return 0.5; // Default if no capture box

  const filtered = getFilteredActivities();
  const boxBounds = captureBox.bounds;

  // Convert capture box pixel coordinates to map lat/lng bounds
  const topLeft = map.containerPointToLatLng([boxBounds.left, boxBounds.top]);
  const bottomRight = map.containerPointToLatLng([boxBounds.left + boxBounds.width, boxBounds.top + boxBounds.height]);
  const mapBounds = L.latLngBounds(topLeft, bottomRight);

  // Count activities that intersect with capture box
  let activitiesInBox = 0;
  filtered.forEach(activity => {
    const polylineStr = activity.map?.summary_polyline;
    if (!polylineStr) return;

    const coords = decodePolyline(polylineStr);
    const hasPointInBox = coords.some(([lat, lng]) => mapBounds.contains([lat, lng]));
    if (hasPointInBox) activitiesInBox++;
  });

  // Calculate opacity based on density
  // Few activities (1-20): high opacity (0.6-0.4)
  // Medium activities (20-100): medium opacity (0.4-0.25)
  // Many activities (100+): low opacity (0.25-0.15)
  if (activitiesInBox <= 20) {
    return Math.max(0.4, 0.6 - (activitiesInBox / 20) * 0.2);
  } else if (activitiesInBox <= 100) {
    return Math.max(0.25, 0.4 - ((activitiesInBox - 20) / 80) * 0.15);
  } else {
    return Math.max(0.15, 0.25 - ((activitiesInBox - 100) / 200) * 0.1);
  }
}

function renderActivities() {
  // Clear existing polylines
  polylines.forEach(p => p.remove());
  polylines = [];

  // Filter activities based on selected types and privacy settings
  const filtered = getFilteredActivities();

  // Calculate optimal opacity based on activity density in capture box
  const baseOpacity = calculateActivityOpacity();

  // Render each activity
  filtered.forEach(activity => {
    const polylineStr = activity.map?.summary_polyline;
    if (!polylineStr) return;

    const coords = decodePolyline(polylineStr);
    if (coords.length === 0) return;

    const colors = getActivityColors();
    const color = colors[activity.type] || colors.default;

    const polyline = L.polyline(coords, {
      color: color,
      weight: 1.5,
      opacity: baseOpacity
    }).addTo(map);

    // Add popup with activity info
    polyline.bindPopup(`
      <strong>${activity.name}</strong><br>
      Type: ${activity.type}<br>
      Distance: ${(activity.distance / 1000).toFixed(2)} km<br>
      Date: ${new Date(activity.start_date).toLocaleDateString()}
    `);

    polylines.push(polyline);
  });

  console.log(`Rendered ${polylines.length} activities`);
}

// Initialize animation
function initializeAnimation() {
  // Filter activities based on selected types and privacy settings
  const filtered = getFilteredActivities();

  // Preserve current time if controller exists
  let preservedTime = null;
  let wasPlaying = false;
  if (animationController) {
    preservedTime = animationController.currentTime;
    wasPlaying = animationController.isPlaying;
    animationController.destroy();
  }

  // Clear static polylines
  polylines.forEach(p => p.remove());
  polylines = [];

  // Calculate optimal opacity based on activity density in capture box
  const baseOpacity = calculateActivityOpacity();

  // Create new animation controller with color function and base opacity
  animationController = new AnimationController(filtered, map, getActivityColors, baseOpacity);

  // Set up callbacks
  animationController.onTimeUpdate = (currentTime) => {
    updateTimeDisplay(currentTime);
    updateTimelineSlider();
  };

  animationController.onActivityAppear = (activity) => {
    console.log('New activity:', activity.name);
  };

  // Restore previous time position if it was set
  if (preservedTime && preservedTime >= animationController.startTime && preservedTime <= animationController.endTime) {
    animationController.seek(preservedTime);
    if (wasPlaying) {
      animationController.play();
      playBtn.disabled = true;
      pauseBtn.disabled = false;
    }
  }

  // Show animation controls
  animationControlsEl.style.display = 'block';

  // Update initial time display and slider position
  updateTimeDisplay(animationController.currentTime);
  updateTimelineSlider();

  // Sync export date range to match the filtered activity range
  if (animationController.startTime && animationController.endTime) {
    exportStartDate.value = formatDateForInput(animationController.startTime);
    exportEndDate.value = formatDateForInput(animationController.endTime);
    updateGifSizeEstimate();
  }

  // Initialize GIF exporter
  gifExporter = new GifExporter(animationController, map);

  // Show export controls
  exportControlsEl.style.display = 'block';
}

function formatDateForInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function updateTimeDisplay(date) {
  if (!date) {
    timeDisplay.textContent = '--/--/----';
    updateDatePreview(); // Update preview even when no date
    return;
  }
  timeDisplay.textContent = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  updateDatePreview(); // Update the date preview on the map
}

function updateTimelineSlider() {
  if (!animationController) return;
  const progress = animationController.getProgress() * 100;
  timelineSlider.value = progress;
}

function updateExportDimensions() {
  // Get current map size
  const mapSize = map.getSize();
  const aspectRatio = mapSize.x / mapSize.y;

  // Set width to 1200 and calculate height to maintain aspect ratio
  const targetWidth = 1200;
  const targetHeight = Math.round(targetWidth / aspectRatio);

  exportWidth.value = targetWidth;
  exportHeight.value = targetHeight;

  console.log(`Export dimensions set to ${targetWidth}x${targetHeight} (aspect ratio: ${aspectRatio.toFixed(2)})`);
}

// Initialize capture box
function initializeCaptureBox() {
  const captureBoxEl = document.getElementById('capture-box');
  const captureOverlay = document.getElementById('capture-overlay');
  updateCaptureBox('max');
  captureBoxEl.classList.add('active');
  captureOverlay.classList.add('active');
  captureOverlay.style.display = 'block'; // Override inline display: none
  setupCaptureBoxResize();
}

// Setup capture box resize and drag functionality
function setupCaptureBoxResize() {
  const captureBoxEl = document.getElementById('capture-box');
  const handles = captureBoxEl.querySelectorAll('.capture-box-handle');
  const mapContainer = map.getContainer();

  // Buffer margin from map edges (in pixels)
  const MARGIN = 20;

  let isResizing = false;
  let isDragging = false;
  let currentHandle = null;
  let startX, startY, startWidth, startHeight, startLeft, startTop;

  // Handle resize from corner handles
  handles.forEach(handle => {
    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();

      isResizing = true;
      currentHandle = handle.classList[1]; // Get the position class (nw, ne, sw, se)

      startX = e.clientX;
      startY = e.clientY;
      startWidth = captureBoxEl.offsetWidth;
      startHeight = captureBoxEl.offsetHeight;
      startLeft = captureBoxEl.offsetLeft;
      startTop = captureBoxEl.offsetTop;

      // Disable map dragging
      map.dragging.disable();

      // Only set to free mode if currently in max mode
      // This preserves aspect ratio locking for square/vertical/horizontal modes
      if (captureBox.ratio === 'max') {
        captureBox.ratio = 'free';

        // Update UI to reflect the mode change
        document.querySelectorAll('.aspect-ratio-pill').forEach(pill => {
          const pillRatio = pill.getAttribute('data-ratio');
          if (pillRatio === 'free') {
            pill.classList.add('selected');
          } else {
            pill.classList.remove('selected');
          }
        });
      }
    });
  });

  // Update cursor based on mouse position over the box
  captureBoxEl.addEventListener('mousemove', (e) => {
    // Skip if currently resizing or dragging
    if (isResizing || isDragging) return;

    // Don't change cursor for handles
    if (e.target.classList.contains('capture-box-handle')) return;

    const rect = captureBoxEl.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Define edge threshold
    const edgeThreshold = 15;
    const isNearLeftEdge = mouseX < edgeThreshold;
    const isNearRightEdge = mouseX > rect.width - edgeThreshold;
    const isNearTopEdge = mouseY < edgeThreshold;
    const isNearBottomEdge = mouseY > rect.height - edgeThreshold;

    const isNearEdge = isNearLeftEdge || isNearRightEdge || isNearTopEdge || isNearBottomEdge;

    // Change cursor based on position
    captureBoxEl.style.cursor = isNearEdge ? 'move' : 'default';
  });

  // Handle drag from box edges only
  captureBoxEl.addEventListener('mousedown', (e) => {
    // Don't start dragging if clicking on a handle
    if (e.target.classList.contains('capture-box-handle')) return;
    // Don't start dragging if clicking on the label
    if (e.target.id === 'capture-box-label' || e.target.closest('#capture-box-label')) return;

    // Calculate relative click position within the box
    const rect = captureBoxEl.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Define edge threshold - only allow dragging from edges (15px from border)
    const edgeThreshold = 15;
    const isNearLeftEdge = clickX < edgeThreshold;
    const isNearRightEdge = clickX > rect.width - edgeThreshold;
    const isNearTopEdge = clickY < edgeThreshold;
    const isNearBottomEdge = clickY > rect.height - edgeThreshold;

    // Only start dragging if click is near an edge
    const isNearEdge = isNearLeftEdge || isNearRightEdge || isNearTopEdge || isNearBottomEdge;

    if (!isNearEdge) {
      // Click is in the middle - don't intercept, let map handle it
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    isDragging = true;
    startX = e.clientX;
    startY = e.clientY;
    startLeft = captureBoxEl.offsetLeft;
    startTop = captureBoxEl.offsetTop;
    startWidth = captureBoxEl.offsetWidth;
    startHeight = captureBoxEl.offsetHeight;

    // Disable map dragging
    map.dragging.disable();

    // Change cursor to move
    document.body.style.cursor = 'move';

    // Dragging doesn't change the ratio mode - aspect ratio is preserved
  });

  document.addEventListener('mousemove', (e) => {
    if (!isResizing && !isDragging) return;

    e.preventDefault();

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    let newWidth = startWidth;
    let newHeight = startHeight;
    let newLeft = startLeft;
    let newTop = startTop;

    if (isDragging) {
      // Dragging the box
      newLeft = startLeft + dx;
      newTop = startTop + dy;

      // Constrain to map bounds with margin
      const mapWidth = mapContainer.clientWidth;
      const mapHeight = mapContainer.clientHeight;

      newLeft = Math.max(MARGIN, Math.min(newLeft, mapWidth - startWidth - MARGIN));
      newTop = Math.max(MARGIN, Math.min(newTop, mapHeight - startHeight - MARGIN));

    } else if (isResizing) {
      // Resizing with handles
      // Determine if aspect ratio should be locked
      const lockAspectRatio = captureBox.ratio === 'square' || captureBox.ratio === 'vertical' || captureBox.ratio === 'horizontal';
      let targetAspectRatio = null;

      if (lockAspectRatio) {
        // Calculate target aspect ratio (width/height)
        if (captureBox.ratio === 'square') {
          targetAspectRatio = 1; // 1:1
        } else if (captureBox.ratio === 'vertical') {
          targetAspectRatio = 9 / 16; // 9:16
        } else if (captureBox.ratio === 'horizontal') {
          targetAspectRatio = 16 / 9; // 16:9
        }
      }

      // For each corner, the opposite corner is the anchor point that stays locked
      // Calculate dimensions based on handle and lock opposite corner in place
      if (lockAspectRatio && targetAspectRatio) {
        // With locked aspect ratio, follow the cursor as closely as possible
        // while maintaining the aspect ratio and keeping opposite corner fixed
        switch (currentHandle) {
          case 'se': // Bottom-right (anchor: top-left)
            // Opposite corner (top-left) stays at (startLeft, startTop)
            // Calculate desired size to reach cursor from anchor
            const desiredWidthSE = Math.abs(e.clientX - startLeft);
            const desiredHeightSE = Math.abs(e.clientY - startTop);

            // Use the dimension that gives us the larger box (closer to cursor)
            if (desiredWidthSE / targetAspectRatio > desiredHeightSE) {
              newHeight = desiredHeightSE;
              newWidth = newHeight * targetAspectRatio;
            } else {
              newWidth = desiredWidthSE;
              newHeight = newWidth / targetAspectRatio;
            }
            // Anchor stays at top-left
            newLeft = startLeft;
            newTop = startTop;
            break;

          case 'sw': // Bottom-left (anchor: top-right)
            // Opposite corner (top-right) stays at (startLeft + startWidth, startTop)
            const anchorRightSW = startLeft + startWidth;
            const desiredWidthSW = Math.abs(anchorRightSW - e.clientX);
            const desiredHeightSW = Math.abs(e.clientY - startTop);

            if (desiredWidthSW / targetAspectRatio > desiredHeightSW) {
              newHeight = desiredHeightSW;
              newWidth = newHeight * targetAspectRatio;
            } else {
              newWidth = desiredWidthSW;
              newHeight = newWidth / targetAspectRatio;
            }
            // Anchor stays at top-right
            newLeft = anchorRightSW - newWidth;
            newTop = startTop;
            break;

          case 'ne': // Top-right (anchor: bottom-left)
            // Opposite corner (bottom-left) stays at (startLeft, startTop + startHeight)
            const anchorBottomNE = startTop + startHeight;
            const desiredWidthNE = Math.abs(e.clientX - startLeft);
            const desiredHeightNE = Math.abs(anchorBottomNE - e.clientY);

            if (desiredWidthNE / targetAspectRatio > desiredHeightNE) {
              newHeight = desiredHeightNE;
              newWidth = newHeight * targetAspectRatio;
            } else {
              newWidth = desiredWidthNE;
              newHeight = newWidth / targetAspectRatio;
            }
            // Anchor stays at bottom-left
            newLeft = startLeft;
            newTop = anchorBottomNE - newHeight;
            break;

          case 'nw': // Top-left (anchor: bottom-right)
            // Opposite corner (bottom-right) stays at (startLeft + startWidth, startTop + startHeight)
            const anchorRightNW = startLeft + startWidth;
            const anchorBottomNW = startTop + startHeight;
            const desiredWidthNW = Math.abs(anchorRightNW - e.clientX);
            const desiredHeightNW = Math.abs(anchorBottomNW - e.clientY);

            if (desiredWidthNW / targetAspectRatio > desiredHeightNW) {
              newHeight = desiredHeightNW;
              newWidth = newHeight * targetAspectRatio;
            } else {
              newWidth = desiredWidthNW;
              newHeight = newWidth / targetAspectRatio;
            }
            // Anchor stays at bottom-right
            newLeft = anchorRightNW - newWidth;
            newTop = anchorBottomNW - newHeight;
            break;
        }
      } else {
        // Free resize (no aspect ratio lock) - follow cursor exactly
        switch (currentHandle) {
          case 'se': // Bottom-right (anchor: top-left)
            newWidth = startWidth + dx;
            newHeight = startHeight + dy;
            newLeft = startLeft;
            newTop = startTop;
            break;
          case 'sw': // Bottom-left (anchor: top-right)
            newWidth = startWidth - dx;
            newHeight = startHeight + dy;
            newLeft = startLeft + dx;
            newTop = startTop;
            break;
          case 'ne': // Top-right (anchor: bottom-left)
            newWidth = startWidth + dx;
            newHeight = startHeight - dy;
            newLeft = startLeft;
            newTop = startTop + dy;
            break;
          case 'nw': // Top-left (anchor: bottom-right)
            newWidth = startWidth - dx;
            newHeight = startHeight - dy;
            newLeft = startLeft + dx;
            newTop = startTop + dy;
            break;
        }
      }

      // Constrain to map bounds with margin
      const mapWidth = mapContainer.clientWidth;
      const mapHeight = mapContainer.clientHeight;

      newWidth = Math.max(100, Math.min(newWidth, mapWidth - newLeft - MARGIN));
      newHeight = Math.max(100, Math.min(newHeight, mapHeight - newTop - MARGIN));
      newLeft = Math.max(MARGIN, Math.min(newLeft, mapWidth - 100 - MARGIN));
      newTop = Math.max(MARGIN, Math.min(newTop, mapHeight - 100 - MARGIN));

      // Re-apply aspect ratio if locked (after constraints)
      if (lockAspectRatio && targetAspectRatio) {
        // Recalculate to maintain aspect ratio after constraints
        const constrainedWidth = newWidth;
        const constrainedHeight = constrainedWidth / targetAspectRatio;

        // Check if height fits
        if (constrainedHeight <= mapHeight - newTop - MARGIN) {
          newHeight = constrainedHeight;
        } else {
          // Height is constrained, recalculate width
          newHeight = Math.min(newHeight, mapHeight - newTop - MARGIN);
          newWidth = newHeight * targetAspectRatio;
        }
      }
    }

    // Apply new dimensions
    captureBoxEl.style.width = `${newWidth}px`;
    captureBoxEl.style.height = `${newHeight}px`;
    captureBoxEl.style.left = `${newLeft}px`;
    captureBoxEl.style.top = `${newTop}px`;

    // Update overlay to match box position
    const captureOverlay = document.getElementById('capture-overlay');
    captureOverlay.style.left = `${newLeft}px`;
    captureOverlay.style.top = `${newTop}px`;
    captureOverlay.style.width = `${newWidth}px`;
    captureOverlay.style.height = `${newHeight}px`;

    // Update label
    const labelEl = document.getElementById('capture-box-label');
    const aspectRatio = (newWidth / newHeight).toFixed(2);
    labelEl.textContent = `${Math.round(newWidth)}×${Math.round(newHeight)} (${aspectRatio}:1)`;

    // Update export dimensions to match capture box
    exportWidth.value = Math.round(newWidth);
    exportHeight.value = Math.round(newHeight);

    // Update size estimate
    updateGifSizeEstimate();

    // Store bounds
    captureBox.bounds = {
      left: newLeft,
      top: newTop,
      width: newWidth,
      height: newHeight
    };
  });

  document.addEventListener('mouseup', () => {
    if (isResizing || isDragging) {
      isResizing = false;
      isDragging = false;
      currentHandle = null;

      // Reset cursor
      captureBoxEl.style.cursor = '';
      document.body.style.cursor = '';

      // Re-enable map dragging
      map.dragging.enable();

      // Update URL after capture box move/resize
      scheduleURLUpdate();
    }
  });
}

// Update capture box based on aspect ratio
function updateCaptureBox(ratio) {
  captureBox.ratio = ratio;

  const mapContainer = map.getContainer();
  const mapWidth = mapContainer.clientWidth;
  const mapHeight = mapContainer.clientHeight;

  // Buffer margin from map edges (in pixels)
  const MARGIN = 20;

  let width, height;

  // Common phone aspect ratios
  const VERTICAL_PHONE = 9 / 16;  // 9:16 (portrait)
  const HORIZONTAL_PHONE = 16 / 9; // 16:9 (landscape)

  switch (ratio) {
    case 'max':
      width = mapWidth - (MARGIN * 2);
      height = mapHeight - (MARGIN * 2);
      break;

    case 'square':
      const availableSize = Math.min(mapWidth, mapHeight) - (MARGIN * 2);
      width = availableSize;
      height = availableSize;
      break;

    case 'vertical':
      height = (mapHeight - MARGIN * 2) * 0.9; // 90% of available height
      width = height * VERTICAL_PHONE;
      if (width > (mapWidth - MARGIN * 2) * 0.9) {
        width = (mapWidth - MARGIN * 2) * 0.9;
        height = width / VERTICAL_PHONE;
      }
      break;

    case 'horizontal':
      width = (mapWidth - MARGIN * 2) * 0.9; // 90% of available width
      height = width / HORIZONTAL_PHONE;
      if (height > (mapHeight - MARGIN * 2) * 0.9) {
        height = (mapHeight - MARGIN * 2) * 0.9;
        width = height * HORIZONTAL_PHONE;
      }
      break;

    case 'free':
      // Keep current dimensions
      const captureBoxEl = document.getElementById('capture-box');
      width = parseInt(captureBoxEl.style.width) || mapWidth;
      height = parseInt(captureBoxEl.style.height) || mapHeight;
      break;
  }

  // Center the box within available space (respecting margins)
  const left = MARGIN + ((mapWidth - MARGIN * 2) - width) / 2;
  const top = MARGIN + ((mapHeight - MARGIN * 2) - height) / 2;

  const captureBoxEl = document.getElementById('capture-box');
  captureBoxEl.style.left = `${left}px`;
  captureBoxEl.style.top = `${top}px`;
  captureBoxEl.style.width = `${width}px`;
  captureBoxEl.style.height = `${height}px`;

  // Update overlay to match box position
  const captureOverlay = document.getElementById('capture-overlay');
  captureOverlay.style.left = `${left}px`;
  captureOverlay.style.top = `${top}px`;
  captureOverlay.style.width = `${width}px`;
  captureOverlay.style.height = `${height}px`;

  // Update label
  const labelEl = document.getElementById('capture-box-label');
  const aspectRatio = (width / height).toFixed(2);
  labelEl.textContent = `${Math.round(width)}×${Math.round(height)} (${aspectRatio}:1)`;

  // Store bounds for export
  captureBox.bounds = {
    left,
    top,
    width,
    height
  };

  // Update export dimensions to match capture box
  exportWidth.value = Math.round(width);
  exportHeight.value = Math.round(height);

  // Update size estimate
  updateGifSizeEstimate();

  // Update URL with new capture box state
  scheduleURLUpdate();
}

// Event listeners
loadBtn.addEventListener('click', () => {
  // Re-fetch activities
  showOnboarding();
});

refreshActivitiesBtn.addEventListener('click', () => {
  // Re-fetch activities from Strava
  showOnboarding();
});

logoutBtn.addEventListener('click', () => {
  // Show logout confirmation dialog
  document.getElementById('logout-confirmation-popup').classList.add('active');
});

// Logout confirmation function (global for onclick handler)
window.confirmLogout = async () => {
  // Clear all auth and cache data
  auth.clearAll();
  await api.clearCache();

  // Hide confirmation popup
  document.getElementById('logout-confirmation-popup').classList.remove('active');

  // Reload the page to restart onboarding
  window.location.reload();
};

// "All Activities" pill handler
activityTypeAll.parentElement.addEventListener('click', (e) => {
  e.preventDefault();

  const checkboxes = document.querySelectorAll('.activity-type-checkbox');
  const pills = document.querySelectorAll('.activity-pill:not(.all-pill)');
  const allPill = activityTypeAll.parentElement;

  if (!activityTypeAll.checked) {
    // Selecting "All" - deselect individual types
    activityTypeAll.checked = true;
    allPill.classList.add('selected');
    checkboxes.forEach(cb => {
      cb.checked = false;
    });
    pills.forEach(pill => {
      pill.classList.remove('selected');
    });
  } else {
    // Deselecting "All" - select all individual types
    activityTypeAll.checked = false;
    allPill.classList.remove('selected');
    checkboxes.forEach(cb => {
      cb.checked = true;
    });
    pills.forEach(pill => {
      pill.classList.add('selected');
    });
  }

  // Update color schemes to show selected types
  populateColorSchemes();

  // Re-render
  if (animationController) {
    initializeAnimation();
  } else {
    renderActivities();
  }

  // Update URL with new selection
  scheduleURLUpdate();
});

// Animation controls
playBtn.addEventListener('click', () => {
  if (!animationController) return;
  animationController.play();
  playBtn.disabled = true;
  pauseBtn.disabled = false;
});

pauseBtn.addEventListener('click', () => {
  if (!animationController) return;
  animationController.pause();
  playBtn.disabled = false;
  pauseBtn.disabled = true;
});

resetBtn.addEventListener('click', () => {
  if (!animationController) return;
  animationController.reset();
  playBtn.disabled = false;
  pauseBtn.disabled = true;
});

timelineSlider.addEventListener('input', (e) => {
  if (!animationController) return;
  const progress = parseFloat(e.target.value) / 100;
  const totalTime = animationController.endTime - animationController.startTime;
  const newTime = new Date(animationController.startTime.getTime() + (totalTime * progress));
  animationController.seek(newTime);
  scheduleURLUpdate(); // Update URL with new time position
});

speedSlider.addEventListener('input', (e) => {
  const speed = parseFloat(e.target.value);
  speedValue.textContent = `${speed} d/s`;
  if (animationController) {
    animationController.setSpeed(speed);
  }
});

// Aspect ratio pill controls
document.querySelectorAll('.aspect-ratio-pill').forEach(pill => {
  pill.addEventListener('click', () => {
    // Deselect all pills
    document.querySelectorAll('.aspect-ratio-pill').forEach(p => p.classList.remove('selected'));

    // Select this pill
    pill.classList.add('selected');

    // Update capture box
    const ratio = pill.getAttribute('data-ratio');
    updateCaptureBox(ratio);
  });
});

// Export dimension controls - maintain capture box aspect ratio
exportWidth.addEventListener('input', () => {
  const captureBoxEl = document.getElementById('capture-box');
  const boxWidth = parseInt(captureBoxEl.style.width);
  const boxHeight = parseInt(captureBoxEl.style.height);
  const aspectRatio = boxWidth / boxHeight;

  const newWidth = parseInt(exportWidth.value);
  exportHeight.value = Math.round(newWidth / aspectRatio);

  updateGifSizeEstimate();
  scheduleURLUpdate();
});

exportHeight.addEventListener('input', () => {
  const captureBoxEl = document.getElementById('capture-box');
  const boxWidth = parseInt(captureBoxEl.style.width);
  const boxHeight = parseInt(captureBoxEl.style.height);
  const aspectRatio = boxWidth / boxHeight;

  const newHeight = parseInt(exportHeight.value);
  exportWidth.value = Math.round(newHeight * aspectRatio);

  updateGifSizeEstimate();
  scheduleURLUpdate();
});

// Estimate GIF file size based on settings
function updateGifSizeEstimate() {
  const width = parseInt(exportWidth.value);
  const height = parseInt(exportHeight.value);
  const duration = parseInt(exportDuration.value);
  const fps = parseInt(exportFps.value);

  // Calculate number of frames (including final heatmap frame)
  const frameCount = Math.floor(duration * fps) + 1;

  // Estimate size per frame based on resolution
  // GIF compression is limited for complex map data with many colors:
  // - Map tiles and routes have high color variance, reducing compression effectiveness
  // - Quality=10 setting produces larger files
  // - Empirically tested: ~0.7 bytes per pixel for typical map content
  const pixels = width * height;
  const bytesPerPixel = 0.7; // Realistic estimate based on actual GIF output
  const frameOverhead = 2048; // Frame header and metadata
  const globalOverhead = 50000; // GIF header, color tables, etc.

  const estimatedBytesPerFrame = (pixels * bytesPerPixel) + frameOverhead;
  const estimatedTotalBytes = (estimatedBytesPerFrame * frameCount) + globalOverhead;

  // Convert to human-readable format
  const estimatedMB = estimatedTotalBytes / (1024 * 1024);

  let displayText;
  if (estimatedMB < 1) {
    displayText = `~${Math.round(estimatedMB * 1024)} KB`;
  } else {
    displayText = `~${estimatedMB.toFixed(1)} MB`;
  }

  // Add warning for large files
  if (estimatedMB > 50) {
    displayText += ' ⚠️';
    sizeEstimateValue.style.color = '#d9534f';
  } else if (estimatedMB > 20) {
    sizeEstimateValue.style.color = '#f0ad4e';
  } else {
    sizeEstimateValue.style.color = '#0066cc';
  }

  sizeEstimateValue.textContent = displayText;
}

// Update estimate when export settings change (width/height handled above with aspect ratio)
exportDuration.addEventListener('input', () => {
  updateGifSizeEstimate();
  scheduleURLUpdate();
});
exportFps.addEventListener('change', () => {
  updateGifSizeEstimate();
  scheduleURLUpdate();
});
exportStartDate.addEventListener('change', scheduleURLUpdate);
exportEndDate.addEventListener('change', scheduleURLUpdate);

// Date filter controls
filterStartDate.addEventListener('change', handleDateFilterChange);
filterEndDate.addEventListener('change', handleDateFilterChange);
dateFilterResetBtn.addEventListener('click', () => {
  // Reset to full range (re-initialize)
  initializeDateFilter();
  handleDateFilterChange();
});

// Date overlay controls
includeDateOverlay.addEventListener('change', () => {
  dateCornerSelector.style.display = includeDateOverlay.checked ? 'block' : 'none';
  updateDatePreview();
  scheduleURLUpdate();
});

// Corner selector options
document.querySelectorAll('.corner-option').forEach(option => {
  option.addEventListener('click', () => {
    document.querySelectorAll('.corner-option').forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');
    updateDatePreview();
    scheduleURLUpdate();
  });
});

// Date format selector
dateFormatSelect.addEventListener('change', () => {
  updateDatePreview();
  scheduleURLUpdate();
});

// Initialize the estimate on load
updateGifSizeEstimate();

// Export controls
exportBtn.addEventListener('click', async () => {
  if (!gifExporter) return;

  try {
    // Get export parameters
    // Parse date inputs as local midnight (not UTC midnight)
    const parseLocalDate = (str) => { const [y, m, d] = str.split('-'); return new Date(+y, +m - 1, +d); };
    const startDate = parseLocalDate(exportStartDate.value);
    const endDate = parseLocalDate(exportEndDate.value);
    const duration = parseInt(exportDuration.value);
    const width = parseInt(exportWidth.value);
    const height = parseInt(exportHeight.value);
    const fps = parseInt(exportFps.value);

    // Validate dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      alert('Please select valid start and end dates');
      return;
    }

    if (startDate >= endDate) {
      alert('Start date must be before end date');
      return;
    }

    // Show progress
    exportProgress.style.display = 'block';
    exportBtn.disabled = true;
    exportBtn.textContent = 'Creating...';

    // Set up progress callback
    gifExporter.onProgress = (percent, message) => {
      progressFill.style.width = `${percent}%`;
      progressFill.textContent = `${Math.round(percent)}%`;
      exportStatus.textContent = message;
    };

    // Export GIF with capture box bounds and date overlay settings
    const blob = await gifExporter.export({
      startDate,
      endDate,
      duration,
      width,
      height,
      fps,
      quality: 10,
      captureBox: captureBox.bounds, // Pass the capture box bounds
      dateOverlay: includeDateOverlay.checked ? {
        enabled: true,
        corner: getSelectedDateCorner(),
        color: getDominantActivityColor(),
        format: dateFormatSelect.value
      } : { enabled: false },
      includeHeatmapFrame: includeHeatmapFrame.checked
    });

    // Hide progress, re-enable button
    exportProgress.style.display = 'none';
    exportBtn.disabled = false;
    exportBtn.textContent = 'Create GIF';

    // Open GIF in a new tab
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');

    console.log(`GIF ready! Size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);

  } catch (error) {
    console.error('Export failed:', error);
    alert(`Export failed: ${error.message}`);
    exportProgress.style.display = 'none';
    exportBtn.disabled = false;
    exportBtn.textContent = 'Create GIF';
  }
});

// ============================================================================
// URL State Management
// ============================================================================

/**
 * Encode current application state to URL hash
 */
function encodeStateToURL() {
  if (!map || !activities.length) return; // Don't encode if not fully initialized

  const params = new URLSearchParams();

  // Map state
  const center = map.getCenter();
  params.set('lat', center.lat.toFixed(6));
  params.set('lng', center.lng.toFixed(6));
  params.set('zoom', map.getZoom());

  // Capture box state
  params.set('ratio', captureBox.ratio);
  if (captureBox.bounds) {
    params.set('boxLeft', Math.round(captureBox.bounds.left));
    params.set('boxTop', Math.round(captureBox.bounds.top));
    params.set('boxWidth', Math.round(captureBox.bounds.width));
    params.set('boxHeight', Math.round(captureBox.bounds.height));
  }

  // Activity type filters
  const selectedTypes = getSelectedActivityTypes();
  if (selectedTypes !== 'all') {
    params.set('types', selectedTypes.join(','));
  }

  // Date filter (only store if inputs exist and have values)
  if (filterStartDate && filterStartDate.value) {
    params.set('filterStart', filterStartDate.value);
  }
  if (filterEndDate && filterEndDate.value) {
    params.set('filterEnd', filterEndDate.value);
  }

  // GIF export settings
  params.set('gifWidth', exportWidth.value);
  params.set('gifHeight', exportHeight.value);
  params.set('gifDuration', exportDuration.value);
  params.set('gifFps', exportFps.value);
  if (exportStartDate.value) {
    params.set('gifStart', exportStartDate.value);
  }
  if (exportEndDate.value) {
    params.set('gifEnd', exportEndDate.value);
  }

  // Date overlay settings
  if (includeDateOverlay.checked) {
    params.set('dateOverlay', 'true');
    params.set('dateCorner', getSelectedDateCorner());
    params.set('dateFormat', dateFormatSelect.value);
  }

  // Animation current time
  if (animationController && animationController.currentTime) {
    params.set('time', animationController.currentTime.toISOString().split('T')[0]);
  }

  // Update URL without triggering page reload
  window.history.replaceState({}, '', `#${params.toString()}`);
}

/**
 * Restore application state from URL hash
 */
function restoreStateFromURL() {
  const hash = window.location.hash.slice(1); // Remove '#'
  if (!hash) return false;

  const params = new URLSearchParams(hash);
  let stateRestored = false;

  // Map state
  if (params.has('lat') && params.has('lng') && params.has('zoom')) {
    const lat = parseFloat(params.get('lat'));
    const lng = parseFloat(params.get('lng'));
    const zoom = parseInt(params.get('zoom'));
    if (!isNaN(lat) && !isNaN(lng) && !isNaN(zoom)) {
      map.setView([lat, lng], zoom);
      stateRestored = true;
    }
  }

  // Capture box ratio
  if (params.has('ratio')) {
    const ratio = params.get('ratio');
    if (['max', 'square', 'vertical', 'horizontal', 'free'].includes(ratio)) {
      captureBox.ratio = ratio;
      // Update the UI to reflect the ratio
      document.querySelectorAll('.aspect-ratio-pill').forEach(pill => {
        const pillRatio = pill.getAttribute('data-ratio');
        if (pillRatio === ratio) {
          pill.classList.add('selected');
        } else {
          pill.classList.remove('selected');
        }
      });
    }
  }

  // Capture box position (for custom positions)
  if (params.has('boxLeft') && params.has('boxTop') && params.has('boxWidth') && params.has('boxHeight')) {
    const left = parseInt(params.get('boxLeft'));
    const top = parseInt(params.get('boxTop'));
    const width = parseInt(params.get('boxWidth'));
    const height = parseInt(params.get('boxHeight'));

    if (!isNaN(left) && !isNaN(top) && !isNaN(width) && !isNaN(height)) {
      // Store for later application (after capture box is initialized)
      window.pendingCaptureBoxRestore = { left, top, width, height };
      stateRestored = true;
    }
  }

  // Activity type filters
  if (params.has('types')) {
    const types = params.get('types').split(',').filter(t => t.trim());
    if (types.length > 0) {
      // Store for later application (after activity types are populated)
      window.pendingActivityTypesRestore = types;
      stateRestored = true;
    }
  }

  // GIF export settings
  if (params.has('gifWidth')) {
    const width = parseInt(params.get('gifWidth'));
    if (!isNaN(width)) exportWidth.value = width;
  }
  if (params.has('gifHeight')) {
    const height = parseInt(params.get('gifHeight'));
    if (!isNaN(height)) exportHeight.value = height;
  }
  if (params.has('gifDuration')) {
    const duration = parseInt(params.get('gifDuration'));
    if (!isNaN(duration)) exportDuration.value = duration;
  }
  if (params.has('gifFps')) {
    const fps = parseInt(params.get('gifFps'));
    if (!isNaN(fps)) exportFps.value = fps;
  }
  if (params.has('gifStart')) {
    exportStartDate.value = params.get('gifStart');
  }
  if (params.has('gifEnd')) {
    exportEndDate.value = params.get('gifEnd');
  }

  // Date overlay settings
  if (params.has('dateOverlay') && params.get('dateOverlay') === 'true') {
    includeDateOverlay.checked = true;
    dateCornerSelector.style.display = 'block';

    if (params.has('dateCorner')) {
      const corner = params.get('dateCorner');
      document.querySelectorAll('.corner-option').forEach(option => {
        if (option.getAttribute('data-corner') === corner) {
          option.classList.add('selected');
        } else {
          option.classList.remove('selected');
        }
      });
    }

    if (params.has('dateFormat')) {
      const format = params.get('dateFormat');
      if (['YYYY-MM-DD', 'D MMM YYYY', 'D MMMM YYYY', 'MMM YYYY', 'MMMM YYYY'].includes(format)) {
        dateFormatSelect.value = format;
      }
    }
  }

  // Date filter (applied after initializeDateFilter runs)
  if (params.has('filterStart') || params.has('filterEnd')) {
    window.pendingDateFilterRestore = {
      start: params.get('filterStart') || null,
      end: params.get('filterEnd') || null
    };
    stateRestored = true;
  }

  // Animation time
  if (params.has('time')) {
    // Store for later application (after animation is initialized)
    window.pendingAnimationTimeRestore = params.get('time');
    stateRestored = true;
  }

  return stateRestored;
}

/**
 * Debounced URL update to avoid updating too frequently
 */
let urlUpdateTimeout;
function scheduleURLUpdate() {
  clearTimeout(urlUpdateTimeout);
  urlUpdateTimeout = setTimeout(() => {
    encodeStateToURL();
  }, 500); // Update URL 500ms after last state change
}

// Start the app
init();
